import React, { useMemo } from 'react';
import styled from 'styled-components';
import { GrowthNav } from './GrowthNav';
import { useGrowthMonitoredPages, useGrowthDiffs } from '../../hooks/useGrowthPeers';

const Page = styled.div`
  background: var(--background);
  min-height: 100%;
`;

const Card = styled.div`
  padding: 1.25rem;
  border-radius: 14px;
  background: var(--surface);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--outline);
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const DiffList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const DiffItem = styled.li`
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--outline);
  font-size: 0.9rem;
  &:last-child {
    border-bottom: none;
  }
`;

const Chip = styled.span`
  display: inline-block;
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.75rem;
  background: var(--surfaceVariant);
  color: var(--primary);
  margin-right: 0.5rem;
`;

export default function GrowthPeersPage() {
  const { data: pages = [], isLoading: pagesLoading } = useGrowthMonitoredPages();
  const { data: diffs = [], isLoading: diffsLoading } = useGrowthDiffs();

  const peerPages = pages.filter((p) => p.scope === 'peer');
  const peersByName = useMemo(() => {
    const m = new Map<string, typeof peerPages>();
    for (const p of peerPages) {
      const name = p.peer_name || 'Unknown';
      if (!m.has(name)) m.set(name, []);
      m.get(name)!.push(p);
    }
    return m;
  }, [peerPages]);

  const categoryCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const d of diffs) {
      const cat = d.diff_category || 'unknown';
      c[cat] = (c[cat] ?? 0) + 1;
    }
    return c;
  }, [diffs]);

  const pageIdToLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of pages) m.set(p.id, `${p.peer_name || p.scope}: ${p.label}`);
    return m;
  }, [pages]);

  if (pagesLoading || diffsLoading) {
    return (
      <Page>
        <GrowthNav />
        <p>Loading…</p>
      </Page>
    );
  }

  return (
    <Page>
      <GrowthNav />
      <SectionTitle style={{ marginTop: 0 }}>Peers</SectionTitle>
      <Card>
        <SectionTitle>Peer list ({peersByName.size})</SectionTitle>
        <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
          {Array.from(peersByName.entries()).map(([name, list]) => (
            <li key={name}>
              <strong>{name}</strong> — {list.length} page(s):{' '}
              {list.map((p) => (
                <a key={p.id} href={p.url} target="_blank" rel="noopener noreferrer" style={{ marginRight: '0.5rem' }}>
                  {p.label}
                </a>
              ))}
            </li>
          ))}
        </ul>
      </Card>

      <Card>
        <SectionTitle>Change categories (last 50 diffs)</SectionTitle>
        <div style={{ marginBottom: '1rem' }}>
          {Object.entries(categoryCounts).map(([cat, count]) => (
            <Chip key={cat}>
              {cat}: {count}
            </Chip>
          ))}
        </div>
      </Card>

      <Card>
        <SectionTitle>Recent changes</SectionTitle>
        <DiffList>
          {diffs.slice(0, 20).map((d) => (
            <DiffItem key={d.id}>
              <Chip>{d.diff_category}</Chip>
              {pageIdToLabel.get(d.monitored_page_id) ?? d.monitored_page_id} — {d.diff_summary}
              <span style={{ color: 'var(--onSurfaceVariant)', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                {new Date(d.created_at).toLocaleDateString()}
              </span>
            </DiffItem>
          ))}
        </DiffList>
      </Card>
    </Page>
  );
}
